import React, { useState, useEffect } from 'react';
import { Card, Form, Button, message, Spin, Avatar, Row, Col, Input, Select, InputNumber, Checkbox, Progress, Divider, Tabs } from 'antd';
import { ThunderboltOutlined, RobotOutlined } from '@ant-design/icons';
import axios from 'axios';
import apiClient, { scrapeArtist } from '../../server/server';
import { getActiveCountries } from '../../server/configuration/FiltersServer';
import { createProfile } from '../../server/profiles/profilesServer';
import MediaInput from '../../components/ui/MediaInput';
import AIQuickProfileCreator from './AIQuickProfileCreator';

const { Option } = Select;

const ProfileScraper = ({ musicGenres }) => {
  const [profileScrapingLoading, setProfileScrapingLoading] = useState(false);
  const [scrapedProfileData, setScrapedProfileData] = useState(null);
  const [profileForm] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [profileScraperError, setProfileScraperError] = useState(null);

  // Default country/city for all scraped profiles
  const [defaultCountry, setDefaultCountry] = useState(null);
  const [defaultCity, setDefaultCity] = useState(null);
  const [defaultCities, setDefaultCities] = useState([]);
  
  // Media state management
  const [mediaUploadRef, setMediaUploadRef] = useState(null);
  const [bannerUploadRef, setBannerUploadRef] = useState(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  
  // Batch processing state for top tracks
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchCurrentTrack, setBatchCurrentTrack] = useState('');
  const [createdProfile, setCreatedProfile] = useState(null);

  // AI bio generation state
  const [aiBioLoading, setAiBioLoading] = useState(false);

  // AI bio generation function
  const generateAIBio = async () => {
    try {
      setAiBioLoading(true);
      
      // Get current form values for context
      const currentValues = profileForm.getFieldsValue();
      const bio = currentValues.bio || '';
      const username = currentValues.username || '';
      const musicTypes = currentValues.musicTypes || [];
      
      if (!bio.trim()) {
        message.error('Please enter some biography text first');
        return;
      }
      
      // Get music genre names for context
      const selectedGenres = musicGenres
        .filter(genre => musicTypes.includes(genre._id))
        .map(genre => genre.value)
        .join(', ');
      
      // Call AI endpoint
      const response = await apiClient.get('/ai/generate-dj-bio', {
        params: {
          keywords: bio,
          artistName: username,
          genre: selectedGenres
        }
      });
      
      if (response.data && response.data.bio) {
        // Clean up the bio text by removing character count at the end
        let cleanBio = response.data.bio;
        
        // Remove character count pattern like "(Character count: 1000)" or similar
        cleanBio = cleanBio.replace(/\s*\(Character count:\s*\d+\)\s*$/, '');
        cleanBio = cleanBio.replace(/\s*\(Characters:\s*\d+\)\s*$/, '');
        cleanBio = cleanBio.replace(/\s*\(Count:\s*\d+\)\s*$/, '');
        cleanBio = cleanBio.replace(/\s*\(\d+\s*characters?\)\s*$/i, '');
        
        // Remove all emojis and icons
        cleanBio = cleanBio.replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE00}-\u{FE0F}]|[\u{1F100}-\u{1F1FF}]|[\u{1F200}-\u{1F2FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '');
        
        // Also remove any trailing whitespace
        cleanBio = cleanBio.trim();
        
        profileForm.setFieldsValue({ bio: cleanBio });
        message.success('AI biography generated successfully!');
      } else {
        message.error('Failed to generate AI biography');
      }
    } catch (error) {
      console.error('AI Bio Generation Error:', error);
      message.error('Failed to generate AI biography. Please try again.');
    } finally {
      setAiBioLoading(false);
    }
  };

  // Load countries for profile creation
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getActiveCountries();
        console.log('🌍 [ProfileScraper] Countries response:', response);
        // Handle both response.data and response.data.data structures
        const countriesData = response?.data?.data || response?.data || [];
        setCountries(countriesData);
        console.log('🌍 [ProfileScraper] Loaded countries:', countriesData.length);
      } catch (error) {
        console.error('Failed to load countries:', error);
        message.error('Failed to load countries');
      }
    };
    loadCountries();
  }, []);

  const handleCountryChange = (countryCode) => {
    const selectedCountry = countries.find(
      (country) => country.code === countryCode,
    );
    if (selectedCountry) {
      setSelectedCountryName(selectedCountry._id);
      setCities(selectedCountry.cities || []);
      profileForm.setFieldsValue({ city: null });
    }
  };

  // Handle default country change for scraper
  const handleDefaultCountryChange = (countryId) => {
    setDefaultCountry(countryId);
    setDefaultCity(null);

    if (countryId) {
      const selectedCountry = countries.find(country => country._id === countryId);
      if (selectedCountry) {
        setDefaultCities(selectedCountry.cities || []);
      }
    } else {
      setDefaultCities([]);
    }
  };

  // Handle default city change for scraper
  const handleDefaultCityChange = (cityId) => {
    setDefaultCity(cityId);
  };

  const handleCreateProfile = async (values) => {
    // Prevent multiple simultaneous uploads
    if (isMediaUploading) {
      message.warning('Media upload is already in progress. Please wait...');
      return;
    }

    try {
      setProfileScrapingLoading(true);
      setIsMediaUploading(true);
      
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user._id) {
        message.error("User ID not found in localStorage.");
        return;
      }

      let pictureUrl = values.picture || '';
      let bannerUrl = values.banner || '';
      console.log('[Profile Creation] Attempting to upload profile picture:', pictureUrl);
      if (pictureUrl && !pictureUrl.includes('api.decadance.app')) {
        try {
          const uploadResponse = await apiClient.post('/uploads/from-url', {
            imageUrl: pictureUrl
          });
          console.log('[Profile Creation] Upload response:', uploadResponse.data);
          if (uploadResponse.data && uploadResponse.data.data && uploadResponse.data.data.url) {
            pictureUrl = uploadResponse.data.data.url;
          } else {
            throw new Error('Invalid response format from image upload');
          }
        } catch (uploadErr) {
          console.error('[Profile Creation] Failed to upload profile picture:', uploadErr.response?.data || uploadErr);
          message.error(`Failed to upload profile picture: ${uploadErr.response?.statusText || uploadErr.message}`);
          setProfileScrapingLoading(false);
          setIsMediaUploading(false);
          return;
        }
      }

      // Upload banner if any
      console.log('[Profile Creation] Attempting to upload banner:', bannerUrl);
      if (bannerUrl && !bannerUrl.includes('api.decadance.app')) {
        try {
          const uploadResponse = await apiClient.post('/uploads/from-url', {
            imageUrl: bannerUrl
          });
          console.log('[Profile Creation] Banner upload response:', uploadResponse.data);
          if (uploadResponse.data && uploadResponse.data.data && uploadResponse.data.data.url) {
            bannerUrl = uploadResponse.data.data.url;
          } else {
            throw new Error('Invalid response format from banner upload');
          }
        } catch (uploadErr) {
          console.error('[Profile Creation] Failed to upload banner:', uploadErr.response?.data || uploadErr);
          message.error(`Failed to upload banner: ${uploadErr.response?.statusText || uploadErr.message}`);
          setProfileScrapingLoading(false);
          setIsMediaUploading(false);
          return;
        }
      }

      // Upload banner files if any from MediaInput
      let uploadedBannerUrl = bannerUrl;
      if (bannerUploadRef && bannerUploadRef.uploadPendingFiles) {
        try {
          console.log('[Profile Creation] Uploading pending banner files...');
          const newlyUploadedUrls = await bannerUploadRef.uploadPendingFiles();
          console.log('[Profile Creation] Newly uploaded banner files:', newlyUploadedUrls);
          // Remove duplicates and filter out invalid URLs
          const cleanedUrls = [...new Set(newlyUploadedUrls.filter(Boolean))];
          if (cleanedUrls && cleanedUrls.length > 0) {
            uploadedBannerUrl = cleanedUrls[0]; // Use first uploaded file as banner
            console.log('[Profile Creation] Using banner URL:', uploadedBannerUrl);
          }
        } catch (bannerUploadErr) {
          console.error('[Profile Creation] Failed to upload banner files:', bannerUploadErr);
          message.error(`Failed to upload banner files: ${bannerUploadErr.message}`);
          setProfileScrapingLoading(false);
          setIsMediaUploading(false);
          return;
        }
      }

      // Upload media files if any
      let uploadedMediaUrls = values.media || []; // Start with existing media URLs from form
      if (mediaUploadRef && mediaUploadRef.uploadPendingFiles) {
        try {
          console.log('[Profile Creation] Uploading pending media files...');
          const newlyUploadedUrls = await mediaUploadRef.uploadPendingFiles();
          console.log('[Profile Creation] Newly uploaded media files:', newlyUploadedUrls);
          // Combine existing URLs with newly uploaded ones
          uploadedMediaUrls = [...uploadedMediaUrls, ...newlyUploadedUrls];
          // Remove duplicates and filter out invalid URLs
          uploadedMediaUrls = [...new Set(uploadedMediaUrls.filter(Boolean))];
          console.log('[Profile Creation] All media URLs (deduplicated):', uploadedMediaUrls);
        } catch (mediaUploadErr) {
          console.error('[Profile Creation] Failed to upload media files:', mediaUploadErr);
          message.error(`Failed to upload media files: ${mediaUploadErr.message}`);
          setProfileScrapingLoading(false);
          setIsMediaUploading(false);
          return;
        }
      }

      console.log('[Profile Creation] Final media URLs to submit:', uploadedMediaUrls);
      const dataToSubmit = {
        ...values,
        picture: pictureUrl,
        banner: uploadedBannerUrl,
        user: user._id,
        country: selectedCountryName,
        musicTypes: values.musicTypes || [],
        media: uploadedMediaUrls,
        // Social media URLs
        spotifyUrl: values.spotifyUrl || '',
        appleMusicUrl: values.appleMusicUrl || '',
        facebookUrl: values.facebookUrl || '',
        instagramUrl: values.instagramUrl || '',
        tiktokUrl: values.tiktokUrl || '',
        youtubeUrl: values.youtubeUrl || '',
        embedMix: values.embedMix || '',
        // Ensure numeric fields are numbers
        followersInstagram: Number(values.followersInstagram) || 0,
        followersSpotify: Number(values.followersSpotify) || 0,
        followersAppleMusic: Number(values.followersAppleMusic) || 0,
        followersSoundcloud: Number(values.followersSoundcloud) || 0,
        // Ensure boolean fields are booleans
        isVerified: Boolean(values.isVerified),
        isActive: Boolean(values.isActive),
        isTrending: Boolean(values.isTrending),
        isFeatured: Boolean(values.isFeatured),
        isValidated: Boolean(values.isValidated),
        isClaimed: Boolean(values.isClaimed),
      };

      const cleanedDataToSubmit = Object.fromEntries(
        Object.entries(dataToSubmit).filter(([key, value]) => {
          if (typeof value === 'boolean' || typeof value === 'number') return true;
          return value !== '' && value !== null && value !== undefined;
        })
      );
      console.log('[Profile Creation] Cleaned payload to /profiles/create:', cleanedDataToSubmit);
      const response = await createProfile(cleanedDataToSubmit);
      
      if (response) {
        message.success("Profile created successfully!");
        setCreatedProfile(response.data);
        
        // Check if we should add top tracks
        const shouldAddTopTracks = values.addTopTracks !== false; // Default to true
        const itunesId = values.itunesMusicID;
        
        if (shouldAddTopTracks && itunesId) {
          console.log('🎵 [ProfileScraper] Profile created, now adding top tracks...');
          message.info('Profile created! Now adding top tracks...');
          // Add top tracks in batch
          await handleBatchAddTopTracks(response.data, itunesId);
        }
        
        profileForm.resetFields();
        setScrapedProfileData(null);
        setCreatedProfile(null);
      } else {
        // If creation failed, we don't need to update any state since we're using form values
      }
    } catch (error) {
      console.error('[Profile Creation] Error response:', error.response?.data || error);
      message.error('Failed to create profile: ' + (error.response?.data?.message || error.message));
    } finally {
      setProfileScrapingLoading(false);
      setIsMediaUploading(false);
    }
  };

  const onFinishProfileScraping = async (values) => {
    setProfileScrapingLoading(true);
    setScrapedProfileData(null);
    setProfileScraperError(null);
    const url = values.url.trim();
    if (!url.match(/^https?:\/\/(www\.)?songstats\.com\/artist\//)) {
      setProfileScrapingLoading(false);
      setProfileScraperError('Please enter a valid Songstats artist URL.');
      return;
    }
    try {
      const response = await scrapeArtist(url);
      // Log the full response for debugging
      console.log('Scraper API response:', response);
      setScrapedProfileData(response);
      const scraped = response;
      
      // Username: from debug.title before '|', fallback to name
      let username = '';
      if (scraped.debug && scraped.debug.title) {
        const parts = scraped.debug.title.split('|');
        if (parts.length > 0 && parts[0].trim()) {
          username = parts[0].trim();
        }
      }
      if (!username) username = scraped.name || '';

      // Instagram Username: extract from socialLinks array
      let instagramUsername = '';
      if (scraped.socialLinks && Array.isArray(scraped.socialLinks)) {
        const instagramLink = scraped.socialLinks.find(link => link.platform === 'instagram');
        if (instagramLink && instagramLink.handle) {
          instagramUsername = instagramLink.handle.replace('@', ''); // Remove @ symbol if present
        }
      }
      console.log('Extracted Instagram username:', instagramUsername);

      // Music Types: map scraped genres/strings to internal musicGenre IDs
      let musicTypeIds = [];
      if (scraped.genres && Array.isArray(scraped.genres)) {
        const genreNameToId = {};
        musicGenres.forEach(g => { genreNameToId[g.value.toLowerCase()] = g._id; });
        musicTypeIds = scraped.genres
          .map(g => genreNameToId[g.toLowerCase()])
          .filter(Boolean);
      }
      console.log('Mapped music type IDs:', musicTypeIds);

      // iTunes Music ID: extract from socialLinks array (Apple Music)
      let itunesMusicID = '';
      if (scraped.socialLinks && Array.isArray(scraped.socialLinks)) {
        const appleMusicLink = scraped.socialLinks.find(link => link.platform === 'apple_music');
        if (appleMusicLink && appleMusicLink.url) {
          // Extract artist ID from Apple Music URL: https://music.apple.com/us/artist/bedouin/466912252
          const match = appleMusicLink.url.match(/\/artist\/[^\/]+\/(\d+)/);
          if (match && match[1]) {
            itunesMusicID = match[1];
          }
        }
      }
      console.log('Extracted iTunes Music ID:', itunesMusicID);

      // Extract all social media URLs from socialLinks
      let spotifyUrl = '';
      let appleMusicUrl = '';
      let facebookUrl = '';
      let instagramUrl = '';
      let tiktokUrl = '';
      let youtubeUrl = '';
      
      if (scraped.socialLinks && Array.isArray(scraped.socialLinks)) {
        scraped.socialLinks.forEach(link => {
          switch(link.platform) {
            case 'spotify':
              spotifyUrl = link.url || '';
              break;
            case 'apple_music':
              appleMusicUrl = link.url || '';
              break;
            case 'facebook':
              facebookUrl = link.url || '';
              break;
            case 'instagram':
              instagramUrl = link.url || '';
              break;
            case 'tiktok':
              tiktokUrl = link.url || '';
              break;
            case 'youtube':
              youtubeUrl = link.url || '';
              break;
          }
        });
      }

      const formValues = {
        username,
        bio: scraped.biography || '',
        picture: scraped.image || '',
        banner: '', // Songstats doesn't provide banner images, keep it empty
        instagramUsername,
        followersInstagram: scraped.followersInstagram || 0,
        followersSpotify: scraped.followersSpotify || 0,
        followersAppleMusic: scraped.followersAppleMusic || 0,
        followersSoundcloud: scraped.followersSoundcloud || 0,
        profileType: scraped.profileType || 'M',
        profileSubType: scraped.profileSubType || '',
        address: scraped.location || '',
        country: defaultCountry || scraped.country || '', // Use default country if set
        city: defaultCity || scraped.city || '', // Use default city if set
        itunesMusicID,
        musicTypes: musicTypeIds,
        // Social media URLs
        spotifyUrl,
        appleMusicUrl,
        facebookUrl,
        instagramUrl,
        tiktokUrl,
        youtubeUrl,
        embedMix: scraped.embedMix || '',
        isVerified: scraped.isVerified || true,
        isActive: true,
        isTrending: false,
        isFeatured: false,
        isValidated: true,
        isClaimed: false
      };

      profileForm.setFieldsValue(formValues);

      // Update cities dropdown if default country is set
      if (defaultCountry) {
        const selectedCountry = countries.find(c => c._id === defaultCountry);
        if (selectedCountry) {
          setSelectedCountryName(defaultCountry);
          setCities(selectedCountry.cities || []);
        }
      }
    } catch (error) {
      setProfileScraperError(error?.response?.data?.message || error.message || 'Failed to scrape Songstats profile.');
    } finally {
      setProfileScrapingLoading(false);
    }
  };

  // Batch add top tracks for a profile
  const handleBatchAddTopTracks = async (profile, itunesId) => {
    if (!profile || !itunesId) {
      return;
    }

    setBatchProcessing(true);
    setBatchProgress(0);
    setBatchTotal(0);
    setBatchCurrentTrack('');

    try {
      // First, fetch top tracks from iTunes
      console.log('🎵 [ProfileScraper] Fetching top tracks for iTunes ID:', itunesId);
      // Use CORS proxy for development with fallback
      const isDevelopment = window.location.hostname === 'localhost';
      const itunesUrl = `https://itunes.apple.com/lookup?id=${itunesId}&entity=song&limit=10`;
      
      let results = [];
      if (isDevelopment) {
        // Try multiple CORS proxies as fallback
        const corsProxies = [
          'https://corsproxy.io/?',
          'https://cors-anywhere.herokuapp.com/',
          'https://api.allorigins.win/get?url='
        ];
        
        for (const proxy of corsProxies) {
          try {
            console.log(`🎵 [ProfileScraper] Trying proxy: ${proxy}`);
            let finalUrl, responseData;
            
            if (proxy.includes('allorigins')) {
              finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
              const response = await axios.get(finalUrl, { timeout: 10000 });
              responseData = JSON.parse(response.data.contents);
            } else {
              finalUrl = `${proxy}${itunesUrl}`;
              const response = await axios.get(finalUrl, { timeout: 10000 });
              responseData = response.data;
            }
            
            results = responseData.results || [];
            console.log(`🎵 [ProfileScraper] Success with proxy: ${proxy}, found ${results.length} results`);
            break;
          } catch (error) {
            console.log(`🎵 [ProfileScraper] Failed with proxy: ${proxy}`, error.message);
            continue;
          }
        }
        
        if (results.length === 0) {
          console.log('🎵 [ProfileScraper] All proxies failed, tracks may not load');
        }
      } else {
        // Production - direct call
        const response = await axios.get(itunesUrl);
        results = response.data.results || [];
      }
      
      // The first result is the artist, the rest are tracks
      const tracks = results.filter(r => r.wrapperType === 'track');
      
      if (tracks.length === 0) {
        message.info('No top tracks found for this artist');
        setBatchProcessing(false);
        return;
      }

      setBatchTotal(tracks.length);
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        setBatchCurrentTrack(track.trackName);
        setBatchProgress(i + 1);

        try {
          console.log(`🎵 [ProfileScraper Batch] Processing track ${i + 1}/${tracks.length}: ${track.trackName}`);

          // Convert iTunes track to API payload format
          const payload = {
            // Required fields
            title: track.trackName,
            artist: track.artistName,
            musicTypes: [], // Will be populated below
            creatorProfileId: profile._id,
            
            // Optional fields
            flyer: track.artworkUrl100?.replace('100x100', '600x600') || null,
            buyLink: track.trackViewUrl || null,
            streamLink: track.previewUrl || null,
            label: track.recordLabel || track.artistName || null,
            releaseDate: track.releaseDate ? new Date(track.releaseDate).toISOString() : null,
            duration: track.trackTimeMillis ? Math.floor(track.trackTimeMillis / 1000) : null,
            trackType: track.wrapperType === 'track' ? 'Single' : 'Album',
            originCountry: track.country || null,
            isTrending: false,
            moodTags: [],
            affiliatedProfiles: [],
          };

          // Map iTunes genre to music types
          if (track.primaryGenreName) {
            const foundGenre = musicGenres.find(g => g.value.toLowerCase() === track.primaryGenreName.toLowerCase());
            if (foundGenre) {
              payload.musicTypes = [foundGenre._id];
            }
          }

          // If no musicTypes from iTunes, use profile's musicTypes
          if (payload.musicTypes.length === 0 && profile.musicTypes && profile.musicTypes.length > 0) {
            payload.musicTypes = profile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
          }

          // Remove null values to clean up the payload
          Object.keys(payload).forEach(key => {
            if (payload[key] === null || payload[key] === undefined) {
              delete payload[key];
            }
          });

          // Make API call
          const response = await apiClient.post('/tracks/backoffice/create', payload);
          
          if (response && response.data) {
            successCount++;
            console.log(`✅ [ProfileScraper Batch] Successfully added: ${track.trackName}`);
          }
        } catch (error) {
          errorCount++;
          console.error(`❌ [ProfileScraper Batch] Failed to add track: ${track.trackName}`, error);
        }

        // Wait 1 second before next track (except for the last one)
        if (i < tracks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Show results
      if (successCount > 0) {
        message.success(`Successfully added ${successCount} top tracks to the profile!`);
      }
      if (errorCount > 0) {
        message.error(`Failed to add ${errorCount} tracks. Check console for details.`);
      }

    } catch (error) {
      console.error('🎵 [ProfileScraper] Error fetching iTunes tracks:', error);
      message.error('Failed to fetch iTunes tracks for batch processing');
    } finally {
      // Reset batch state
      setBatchProcessing(false);
      setBatchProgress(0);
      setBatchTotal(0);
      setBatchCurrentTrack('');
    }
  };

  const tabItems = [
    {
      key: '1',
      label: (
        <span>
          <ThunderboltOutlined />
          Songstats Scraper
        </span>
      ),
      children: (
        <div>
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              {/* Default Location Filter */}
              <Card title="Default Profile Location" className="mb-4">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Default Country">
                  <Select
                    placeholder="Select default country"
                    value={defaultCountry}
                    onChange={handleDefaultCountryChange}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {countries?.map(country => (
                      <Option key={country._id} value={country._id}>
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Default City">
                  <Select
                    placeholder="Select default city"
                    value={defaultCity}
                    onChange={handleDefaultCityChange}
                    allowClear
                    disabled={!defaultCountry}
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {defaultCities?.map(city => (
                      <Option key={city._id} value={city._id}>
                        {city.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <div style={{ fontSize: '12px', color: '#888' }}>
              When set, all scraped profiles will automatically use these default country and city values.
            </div>
          </Card>

          <Card title="Scrape Songstats Artist Profile" className="mb-4">
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Supported Platform:</h4>
              <div className="text-xs text-blue-600 space-y-1">
                <div>• Songstats Artist URLs only (e.g. https://songstats.com/artist/...)</div>
              </div>
            </div>
            <Form
              onFinish={onFinishProfileScraping}
              layout="vertical"
            >
              <Form.Item
                name="url"
                label="Songstats Artist URL"
                rules={[
                  { required: true, message: 'Please input the Songstats artist URL!' },
                  { type: 'url', message: 'Please enter a valid URL!' }
                ]}
              >
                <Input 
                  placeholder="https://songstats.com/artist/..." 
                  size="large"
                />
              </Form.Item>
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={profileScrapingLoading}
                  size="large"
                  block
                >
                  Scrape Songstats Profile
                </Button>
              </Form.Item>
            </Form>
            {profileScrapingLoading && (
              <div className="text-center mt-4">
                <Spin size="large" />
                <p className="mt-2">Extracting Songstats profile information...</p>
              </div>
            )}
            {profileScraperError && (
              <div className="text-center mt-4 text-red-600">
                {profileScraperError}
              </div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          {scrapedProfileData && (
            <Card title="Scraped Songstats Artist Data" className="mb-4">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {scrapedProfileData.image && (
                  <Avatar 
                    src={scrapedProfileData.image}
                    size={80}
                    style={{ marginBottom: 12 }}
                  />
                )}
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8, textAlign: 'center' }}>
                  {(() => {
                    if (scrapedProfileData.debug && scrapedProfileData.debug.title) {
                      const parts = scrapedProfileData.debug.title.split('|');
                      if (parts.length > 0 && parts[0].trim()) {
                        return parts[0].trim();
                      }
                    }
                    return 'Unknown Artist';
                  })()}
                </div>
                {scrapedProfileData.biography && (
                  <div style={{ color: '#666', fontSize: 14, marginBottom: 12, textAlign: 'center', maxWidth: 400, whiteSpace: 'pre-line' }}>
                    {scrapedProfileData.biography}
                  </div>
                )}
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Profile Creation Form */}
      {scrapedProfileData && (
        <div style={{ maxWidth: 900, margin: '32px auto' }}>
          <Form
            form={profileForm}
            layout="vertical"
            onFinish={handleCreateProfile}
            initialValues={{
              isActive: true,
              isVerified: true,
              isTrending: false,
              isFeatured: false,
              isValidated: true,
              isClaimed: false,
              profileType: 'M',
              addTopTracks: true
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="username"
                  label="Username"
                  rules={[{ required: true, message: 'Username is required' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="instagramUsername"
                  label="Instagram Username"
                  rules={[{ required: true, message: 'Instagram username is required' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item 
              name="bio" 
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Biography</span>
                  <Button
                    type="primary"
                    size="small"
                    icon={<span>🤖</span>}
                    loading={aiBioLoading}
                    onClick={generateAIBio}
                    style={{ marginLeft: 8 }}
                  >
                    AI Generate
                  </Button>
                </div>
              }
            >
              <Input.TextArea rows={3} />
            </Form.Item>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="profileType"
                  label="Profile Type"
                  rules={[{ required: true, message: 'Profile type is required' }]}
                >
                  <Select>
                    <Option value="M">Musician</Option>
                    <Option value="L">Label</Option>
                    <Option value="MG">Magazine</Option>
                    <Option value="PRA">PR Agency</Option>
                    <Option value="T">Talent Manager</Option>
                    <Option value="EM">Event Manager</Option>
                    <Option value="NC">Night Club</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="profileSubType" label="Profile Sub Type">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="picture" label="Profile Picture URL">
                  <Input placeholder="https://..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="banner" label="Banner (Image Upload or URL)">
                  <MediaInput
                    maxCount={1}
                    value={profileForm.getFieldValue('banner') ? [profileForm.getFieldValue('banner')] : []}
                    onChange={(urls) => {
                      console.log('📋 [Profile Form] Banner onChange called with URLs:', urls);
                      profileForm.setFieldsValue({ banner: urls && urls.length > 0 ? urls[0] : '' });
                    }}
                    onFilesReady={(uploadRef) => {
                      console.log('📋 [Profile Form] Banner upload reference received');
                      setBannerUploadRef(uploadRef);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="country" label="Country">
                  <Select
                    placeholder="Select country"
                    onChange={handleCountryChange}
                    showSearch
                    filterOption={(input, option) => {
                      const country = countries.find(c => c.code === option.value);
                      return country?.name?.toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {countries.map(country => (
                      <Option key={country._id} value={country.code}>
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="city" label="City">
                  <Select 
                    placeholder="Select city" 
                    showSearch
                    filterOption={(input, option) => {
                      const city = cities.find(c => c._id === option.value);
                      return city?.name?.toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {cities.map(city => (
                      <Option key={city._id} value={city._id}>
                        {city.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item
              name="musicTypes"
              label="Music Types"
              rules={[{ required: true, message: 'Please select at least one music type' }]}
            >
              <Select 
                mode="multiple" 
                placeholder="Select music types"
                showSearch
                filterOption={(input, option) => {
                  const genre = musicGenres.find(g => g._id === option.value);
                  return genre?.value?.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {musicGenres
                  .sort((a, b) => a.value.localeCompare(b.value))
                  .map(type => (
                    <Option key={type._id} value={type._id}>{type.value}</Option>
                  ))}
              </Select>
            </Form.Item>
            
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="followersInstagram" label="Instagram Followers">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="followersSpotify" label="Spotify Followers">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="followersAppleMusic" label="Apple Music Followers">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="followersSoundcloud" label="SoundCloud Followers">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item name="itunesMusicID" label="iTunes Music ID">
              <Input placeholder="Enter iTunes Music ID" />
            </Form.Item>
            
            {/* Social Media URLs */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="spotifyUrl" label="Spotify URL">
                  <Input placeholder="https://spotify.com/..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="appleMusicUrl" label="Apple Music URL">
                  <Input placeholder="https://music.apple.com/..." />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="instagramUrl" label="Instagram URL">
                  <Input placeholder="https://instagram.com/..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="facebookUrl" label="Facebook URL">
                  <Input placeholder="https://facebook.com/..." />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="tiktokUrl" label="TikTok URL">
                  <Input placeholder="https://tiktok.com/@..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="youtubeUrl" label="YouTube URL">
                  <Input placeholder="https://youtube.com/..." />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item name="embedMix" label="Embed Mix URL (SoundCloud/Mixcloud)">
              <Input placeholder="https://soundcloud.com/... or embed code" />
            </Form.Item>
            
            <Form.Item 
              name="addTopTracks" 
              valuePropName="checked" 
              initialValue={true}
              style={{ marginBottom: 16 }}
            >
              <Checkbox>
                Add TOP tracks automatically after profile creation
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {profileForm.getFieldValue('itunesMusicID') ? 
                    'Will fetch and create track publications for the top 10 tracks from iTunes' : 
                    'Requires iTunes Music ID to be filled'
                  }
                </div>
              </Checkbox>
            </Form.Item>
            
            <Form.Item name="media" label="Media (Images/Videos)">
              <MediaInput
                maxCount={10}
                acceptedTypes={['image/*', 'video/*']}
                value={profileForm.getFieldValue('media') || []}
                onChange={(urls) => {
                  console.log('📋 [Profile Form] MediaInput onChange called with URLs:', urls);
                  profileForm.setFieldsValue({ media: urls });
                }}
                onFilesReady={(uploadRef) => {
                  console.log('📋 [Profile Form] MediaInput upload reference received');
                  setMediaUploadRef(uploadRef);
                }}
              />
            </Form.Item>
            
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="isVerified" valuePropName="checked">
                  <Checkbox>Verified</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isTrending" valuePropName="checked">
                  <Checkbox>Trending</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isFeatured" valuePropName="checked">
                  <Checkbox>Featured</Checkbox>
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="isValidated" valuePropName="checked">
                  <Checkbox>Validated</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isClaimed" valuePropName="checked">
                  <Checkbox>Claimed</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isActive" valuePropName="checked">
                  <Checkbox>Active</Checkbox>
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={profileScrapingLoading || batchProcessing || isMediaUploading}
                size="large"
              >
                {batchProcessing ? 'Adding Top Tracks...' : 
                 isMediaUploading ? 'Uploading Media...' : 
                 'Create Profile'}
              </Button>
              <Button 
                className="ml-3" 
                onClick={() => {
                  profileForm.resetFields();
                  setScrapedProfileData(null);
                }}
                size="large"
                disabled={batchProcessing || isMediaUploading}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
          
          {/* Batch Processing Progress */}
          {batchProcessing && (
            <Card title="Adding Top Tracks" style={{ marginTop: 24 }}>
              <div className="text-center" style={{ padding: '20px 0' }}>
                <Spin size="large" />
                <p className="mt-2">Adding top tracks automatically...</p>
                <p className="text-sm text-gray-600">
                  Processing: {batchCurrentTrack}
                </p>
                <Progress
                  percent={Math.round((batchProgress / batchTotal) * 100)}
                  status="active"
                  format={(percent) => `${batchProgress}/${batchTotal} (${percent}%)`}
                  style={{ marginTop: 16 }}
                />
              </div>
            </Card>
          )}
        </div>
      )}
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <span>
          <RobotOutlined />
          AI Quick Creator
        </span>
      ),
      children: <AIQuickProfileCreator musicGenres={musicGenres} />,
    },
  ];

  return (
    <div>
      <Tabs items={tabItems} size="large" />
    </div>
  );
};

export default ProfileScraper;
